/**
 * Rent Refund Modal Alpine.js Component
 *
 * Refunds rent to a tenant that was already remitted to the landlord(s).
 * Kicked off from the tenancy kebab. Charges the amount back to the landlord(s)
 * as a negative rent line and parks it as a tenant claim until the landlord
 * funds it (reconciled as Money on account). See Accounting::RentRefund.
 */

export function rentRefundModal() {
  return {
    open: false,

    // Form state
    netAmountPounds: '',
    description: '',

    // Context
    tenancyId: null,

    // Submit state
    submitting: false,
    error: null,
    successMessage: null,

    openModal(detail) {
      const store = Alpine.store?.('slideovers')

      this.resetForm()
      this.tenancyId = detail?.tenancyId || null
      this.open = true

      if (store?.register) {
        store.register('rent-refund-modal', 'new', this)
      }
    },

    close() {
      const store = Alpine.store?.('slideovers')
      if (store) {
        store.unregister('rent-refund-modal', 'new')
      }
      this.$nextTick(() => {
        this.open = false
      })
    },

    resetForm() {
      this.netAmountPounds = ''
      this.description = ''
      this.error = null
      this.successMessage = null
      this.submitting = false
    },

    async submit() {
      if (!this.canSubmit || this.submitting) return

      this.submitting = true
      this.error = null
      this.successMessage = null

      const body = {
        rent_refund: {
          tenancy_id: this.tenancyId,
          net_amount_pence: this.netAmountPence,
          description: this.description
        }
      }

      try {
        const response = await fetch('/accounting/accounts/rent_refunds.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          body: JSON.stringify(body)
        })

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.error || 'Failed to raise rent refund')
        }

        this.successMessage = 'Rent refund raised. Reconcile the landlord’s payment as Money on account to complete it.'
        window.dispatchEvent(new CustomEvent('rent-refund-created', { detail: data }))
        setTimeout(() => this.close(), 2500)
      } catch (e) {
        console.error('Failed to raise rent refund:', e)
        this.error = e.message || 'Failed to raise rent refund. Please try again.'
      } finally {
        this.submitting = false
      }
    },

    // Computed
    get netAmountPence() {
      const pounds = parseFloat(this.netAmountPounds)
      if (isNaN(pounds) || pounds <= 0) return 0
      return Math.round(pounds * 100)
    },

    get canSubmit() {
      if (!this.tenancyId) return false
      if (!this.description.trim()) return false
      if (this.netAmountPence <= 0) return false
      return true
    },

    get csrfToken() {
      return document.querySelector('meta[name="csrf-token"]')?.content
    },

    formatPence(pence) {
      if (pence === null || pence === undefined) return '-'
      const absPence = Math.abs(pence)
      const pounds = (absPence / 100).toFixed(2)
      const formatted = parseFloat(pounds).toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
      return '£' + formatted
    }
  }
}
